import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Grid, Alert, CircularProgress,
  Avatar, Button, TextField, MenuItem, Tooltip, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Tab, Tabs, Divider,
} from '@mui/material';
import { AccountBalance, Download, Refresh, ExpandMore, ExpandLess } from '@mui/icons-material';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const YEAR = new Date().getFullYear();

function fmt(n: number) {
  return `HKD ${Math.round(n).toLocaleString('en-HK')}`;
}

const COMPONENT_CFG = [
  { key: 'baseSalaryMonthly',        label: 'Salary',        color: '#C08A2E' },
  { key: 'mpfEmployerMonthly',       label: 'MPF',           color: '#1565c0' },
  { key: 'benefitsCostMonthly',      label: 'Benefits',      color: '#2e7d32' },
  { key: 'trainingCostMonthly',      label: 'Training',      color: '#6a1b9a' },
  { key: 'assetDepreciationMonthly', label: 'Assets',        color: '#e65100' },
];

interface EmployeeCostRow {
  employeeId: string; fullName: string; department: string; position: string;
  employmentType: string; tenureMonths: number;
  baseSalaryMonthly: number; mpfEmployerMonthly: number; benefitsCostMonthly: number;
  trainingCostMonthly: number; assetDepreciationMonthly: number;
  totalMonthly: number; totalAnnual: number;
  salaryPct: number; mpfPct: number; benefitsPct: number; trainingPct: number; assetPct: number;
}

function CostBar({ row, maxTotal }: { row: EmployeeCostRow; maxTotal: number }) {
  const pct = maxTotal > 0 ? (row.totalMonthly / maxTotal) * 100 : 0;
  return (
    <Box sx={{ display: 'flex', gap: 0, height: 8, borderRadius: 4, overflow: 'hidden', width: `${pct}%`, minWidth: 4 }}>
      {COMPONENT_CFG.map(c => {
        const compPct = row.totalMonthly > 0 ? ((row as any)[c.key] / row.totalMonthly) * 100 : 0;
        return compPct > 0 ? <Box key={c.key} sx={{ height: '100%', width: `${compPct}%`, bgcolor: c.color }} /> : null;
      })}
    </Box>
  );
}

export default function CostPerEmployee() {
  const [year, setYear]       = useState(YEAR);
  const [tab, setTab]         = useState(0);
  const [data, setData]       = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [expanded, setExpanded]     = useState<string | null>(null);
  const [detailEmp, setDetailEmp]   = useState<EmployeeCostRow | null>(null);
  const [sortBy, setSortBy]         = useState<'totalAnnual' | 'fullName' | 'department'>('totalAnnual');
  const [sortDir, setSortDir]       = useState<'asc' | 'desc'>('desc');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await apiFetch(`/api/cost-per-employee?year=${year}`);
      setData(d);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const resp = await fetch(`${API}/api/cost-per-employee/export?year=${year}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    const blob = await resp.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url;
    a.download = `cost-per-employee-${year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const employees: EmployeeCostRow[] = data?.employees ?? [];
  const depts  = [...Object.keys(Object.fromEntries(employees.map(e => [e.department, 1])))].sort();
  const types  = [...Object.keys(Object.fromEntries(employees.map(e => [e.employmentType, 1])))].sort();

  const filtered = employees
    .filter(e => (!deptFilter || e.department === deptFilter) && (!typeFilter || e.employmentType === typeFilter))
    .sort((a, b) => {
      const va = (a as any)[sortBy]; const vb = (b as any)[sortBy];
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return sortDir === 'desc' ? -cmp : cmp;
    });

  const maxTotal = Math.max(...filtered.map(e => e.totalMonthly), 1);
  const cc       = data?.costComponents;

  const toggleSort = (col: typeof sortBy) => {
    if (sortBy === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#2e7d32', width: 48, height: 48 }}><AccountBalance /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Cost Per Employee</Typography>
            <Typography variant="body2" color="text.secondary">
              Fully loaded employment cost: salary + MPF + benefits + training + assets
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField select size="small" label="Year" value={year} onChange={e => setYear(parseInt(e.target.value))} sx={{ width: 100 }}>
            {[YEAR - 1, YEAR, YEAR + 1].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
          <Tooltip title="Export CSV"><IconButton onClick={exportCsv}><Download /></IconButton></Tooltip>
          <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : !data ? null : (
        <>
          {/* KPI row */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Active Employees',     value: data.totalHeadcount,                    fmt: (v: number) => v.toString(),     color: '#C08A2E' },
              { label: 'Total Annual Cost',    value: data.totalAnnualCost,                   fmt: fmt,                             color: '#2e7d32' },
              { label: 'Avg Cost / Employee',  value: data.avgCostPerEmployee,                fmt: fmt,                             color: '#1565c0' },
              { label: 'Median Cost / Employee', value: data.medianCostPerEmployee,           fmt: fmt,                             color: '#6a1b9a' },
              { label: 'Salary % of Total',    value: cc?.salaryPct,                          fmt: (v: number) => `${v}%`,          color: '#C08A2E' },
              { label: 'Non-Salary Overhead',  value: cc ? 100 - cc.salaryPct : 0,            fmt: (v: number) => `${v}%`,          color: '#e65100' },
            ].map(k => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} key={k.label}>
                <Paper sx={{ p: 1.5, textAlign: 'center', borderTop: `3px solid ${k.color}` }}>
                  <Typography variant="h5" fontWeight={700} color={k.color}>{k.fmt(k.value ?? 0)}</Typography>
                  <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Component waterfall */}
          {cc && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Annual Cost Components (All Employees)</Typography>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                {COMPONENT_CFG.map(c => {
                  const total = cc['total' + c.label] ?? cc['total' + c.key.replace('Monthly','').replace(/^\w/, (x: string) => x.toUpperCase())] ?? 0;
                  const pct   = cc[(c.key.replace('Monthly','') + 'Pct').replace(/^\w/, (x: string) => x.toLowerCase())] ?? 0;
                  const annualKey = 'total' + c.label.charAt(0).toUpperCase() + c.label.slice(1).toLowerCase();
                  const annualVal = cc[annualKey] ?? Object.entries(cc).find(([k]) => k.toLowerCase().includes(c.label.toLowerCase()))?.[1] ?? 0;
                  return (
                    <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={c.key}>
                      <Box sx={{ p: 1, borderRadius: 1, bgcolor: c.color + '12', borderLeft: `3px solid ${c.color}` }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>{c.label}</Typography>
                        <Typography variant="body2" fontWeight={700} color={c.color}>
                          {fmt(annualVal as number)}
                        </Typography>
                        <LinearProgress variant="determinate" value={pct}
                          sx={{ mt: 0.5, height: 4, borderRadius: 2,
                            '& .MuiLinearProgress-bar': { bgcolor: c.color } }} />
                        <Typography variant="caption" color="text.secondary">{pct}% of total</Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          )}

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none' } }}>
            <Tab label={`Employees (${filtered.length})`} />
            <Tab label="By Department" />
            <Tab label="By Employment Type" />
          </Tabs>

          {tab === 0 && (
            <>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField select size="small" label="Department" value={deptFilter} onChange={e => setDeptFilter(e.target.value)} sx={{ minWidth: 160 }}>
                  <MenuItem value="">All</MenuItem>
                  {depts.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Type" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} sx={{ minWidth: 140 }}>
                  <MenuItem value="">All</MenuItem>
                  {types.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Box>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell onClick={() => toggleSort('fullName')} sx={{ cursor: 'pointer', fontWeight: 700 }}>
                        Employee {sortBy === 'fullName' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                      </TableCell>
                      <TableCell onClick={() => toggleSort('department')} sx={{ cursor: 'pointer', fontWeight: 700 }}>
                        Department {sortBy === 'department' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                      </TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Salary/mo</TableCell>
                      <TableCell align="right">MPF/mo</TableCell>
                      <TableCell align="right">Benefits/mo</TableCell>
                      <TableCell align="right">Other/mo</TableCell>
                      <TableCell onClick={() => toggleSort('totalAnnual')} align="right" sx={{ cursor: 'pointer', fontWeight: 700 }}>
                        Total/yr {sortBy === 'totalAnnual' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                      </TableCell>
                      <TableCell>Cost Breakdown</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map(e => (
                      <TableRow key={e.employeeId} hover sx={{ cursor: 'pointer' }}
                        onClick={() => setDetailEmp(e)}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: '#C08A2E' }}>
                              {e.fullName[0]}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{e.fullName}</Typography>
                              <Typography variant="caption" color="text.secondary">{e.position}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell><Typography variant="body2">{e.department}</Typography></TableCell>
                        <TableCell>
                          <Chip size="small" label={e.employmentType?.replace('_', ' ')} sx={{ fontSize: '0.6rem' }} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{e.baseSalaryMonthly.toLocaleString('en-HK')}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="#1565c0">{e.mpfEmployerMonthly.toLocaleString('en-HK')}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="#2e7d32">{e.benefitsCostMonthly.toLocaleString('en-HK')}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="#6a1b9a">
                            {(e.trainingCostMonthly + e.assetDepreciationMonthly).toLocaleString('en-HK')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700} color="#2e7d32">
                            {fmt(e.totalAnnual)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          <CostBar row={e} maxTotal={maxTotal} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {tab === 1 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                    <TableCell align="right">Headcount</TableCell>
                    <TableCell align="right">Avg / Employee / yr</TableCell>
                    <TableCell align="right">Total Annual</TableCell>
                    <TableCell>Share of Payroll</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.byDepartment ?? []).map((d: any) => {
                    const sharePct = data.totalAnnualCost > 0
                      ? Math.round((d.totalAnnual / data.totalAnnualCost) * 100) : 0;
                    return (
                      <TableRow key={d.department} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{d.department}</TableCell>
                        <TableCell align="right">{d.headcount}</TableCell>
                        <TableCell align="right">{fmt(d.avgPerEmployee * 12)}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={700} color="#2e7d32">{fmt(d.totalAnnual)}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress variant="determinate" value={sharePct}
                              sx={{ flex: 1, height: 8, borderRadius: 4,
                                '& .MuiLinearProgress-bar': { bgcolor: '#C08A2E' } }} />
                            <Typography variant="caption" color="#C08A2E" fontWeight={700}>{sharePct}%</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 2 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employment Type</TableCell>
                    <TableCell align="right">Headcount</TableCell>
                    <TableCell align="right">Avg Monthly Cost</TableCell>
                    <TableCell align="right">Total Annual Cost</TableCell>
                    <TableCell>Share</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.byEmploymentType ?? []).map((t: any) => {
                    const sharePct = data.totalAnnualCost > 0
                      ? Math.round((t.totalAnnual / data.totalAnnualCost) * 100) : 0;
                    return (
                      <TableRow key={t.type} hover>
                        <TableCell>
                          <Chip size="small" label={t.type?.replace('_', ' ')} sx={{ textTransform: 'capitalize' }} />
                        </TableCell>
                        <TableCell align="right">{t.headcount}</TableCell>
                        <TableCell align="right">{fmt(t.avgMonthly)}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={700} color="#2e7d32">{fmt(t.totalAnnual)}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress variant="determinate" value={sharePct}
                              sx={{ flex: 1, height: 8, borderRadius: 4,
                                '& .MuiLinearProgress-bar': { bgcolor: '#1565c0' } }} />
                            <Typography variant="caption" color="#1565c0" fontWeight={700}>{sharePct}%</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Employee detail dialog */}
      <Dialog open={!!detailEmp} onClose={() => setDetailEmp(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: '#C08A2E' }}>{detailEmp?.fullName[0]}</Avatar>
          {detailEmp?.fullName}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {detailEmp && (
            <>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Department</Typography>
                  <Typography variant="body2" fontWeight={600}>{detailEmp.department}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Position</Typography>
                  <Typography variant="body2" fontWeight={600}>{detailEmp.position}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Typography variant="body2">{detailEmp.employmentType?.replace('_', ' ')}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Tenure</Typography>
                  <Typography variant="body2">{detailEmp.tenureMonths} months</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Monthly Cost Breakdown</Typography>
              {COMPONENT_CFG.map(c => {
                const val   = (detailEmp as any)[c.key] as number;
                const total = detailEmp.totalMonthly;
                const pct   = total > 0 ? Math.round((val / total) * 100) : 0;
                return (
                  <Box key={c.key} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">{c.label}</Typography>
                      <Typography variant="body2" fontWeight={700} color={c.color}>
                        HKD {val.toLocaleString('en-HK')} <Typography component="span" variant="caption" color="text.secondary">({pct}%)</Typography>
                      </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={pct}
                      sx={{ height: 6, borderRadius: 3, mt: 0.25,
                        '& .MuiLinearProgress-bar': { bgcolor: c.color } }} />
                  </Box>
                );
              })}
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" fontWeight={700}>Total Monthly</Typography>
                <Typography variant="h6" fontWeight={800} color="#2e7d32">{fmt(detailEmp.totalMonthly)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                <Typography variant="subtitle2" fontWeight={700}>Total Annual</Typography>
                <Typography variant="h5" fontWeight={800} color="#2e7d32">{fmt(detailEmp.totalAnnual)}</Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailEmp(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
